import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const scoreData = [
  {
    id: "c1",
    address: "5318 Farnam St",
    matchScore: 96,
    distance: 0.2,
    priceAdjustment: -15000,
    reasons: ["Same street", "Similar sqft", "Same beds/baths"],
    scoreBreakdown: { location: 98, size: 95, condition: 94, features: 97 },
  },
  {
    id: "c2",
    address: "4820 Dodge Street",
    matchScore: 91,
    distance: 0.5,
    priceAdjustment: +8500,
    reasons: ["Same school district", "Similar year built", "Comparable lot"],
    scoreBreakdown: { location: 90, size: 93, condition: 89, features: 92 },
  },
  {
    id: "c3",
    address: "6102 Cass Street",
    matchScore: 88,
    distance: 0.8,
    priceAdjustment: -22000,
    reasons: ["Similar property type", "Close proximity", "Comparable DOM"],
    scoreBreakdown: { location: 85, size: 90, condition: 88, features: 89 },
  },
  {
    id: "c4",
    address: "2215 S 52nd Avenue",
    matchScore: 83,
    distance: 1.1,
    priceAdjustment: +12000,
    reasons: ["Same zip code", "Similar condition", "Comparable garage"],
    scoreBreakdown: { location: 80, size: 85, condition: 84, features: 83 },
  },
  {
    id: "c5",
    address: "7834 Pacific Street",
    matchScore: 79,
    distance: 1.4,
    priceAdjustment: -5000,
    reasons: ["Nearby neighborhood", "Similar features", "Comparable lot size"],
    scoreBreakdown: { location: 76, size: 80, condition: 79, features: 81 },
  },
];

function ScoreBar({ value, color = "var(--color-primary)" }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="font-mono-data text-xs text-muted-foreground w-6 text-right">{value}</span>
    </div>
  );
}

function ScoreColor(score) {
  if (score >= 90) return "text-secondary";
  if (score >= 80) return "text-primary";
  return "text-warning";
}

export default function ComparableScoreCard({ onViewAll }) {
  return (
    <div className="card-base p-0 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Target" size={15} color="var(--color-primary)" />
          <span className="font-heading font-semibold text-sm text-foreground">Comp Scoring</span>
        </div>
        <span className="text-xs font-caption text-muted-foreground">{scoreData?.length} matches</span>
      </div>
      <div className="flex flex-col divide-y divide-border overflow-y-auto" style={{ maxHeight: 520 }}>
        {scoreData?.map((comp) => (
          <div key={comp?.id} className="p-4 hover:bg-muted/40 transition-standard">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-sm font-caption font-medium text-foreground truncate">{comp?.address}</p>
                <p className="text-xs text-muted-foreground font-caption flex items-center gap-1 mt-0.5">
                  <Icon name="MapPin" size={11} color="currentColor" />
                  {comp?.distance} mi away
                </p>
              </div>
              <div className={`text-xl font-mono-data font-bold flex-shrink-0 ${ScoreColor(comp?.matchScore)}`}>
                {comp?.matchScore}%
              </div>
            </div>

            {/* Score breakdown */}
            <div className="space-y-1 mb-2">
              {Object.entries(comp?.scoreBreakdown)?.map(([key, val]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs font-caption text-muted-foreground capitalize w-16 flex-shrink-0">{key}</span>
                  <ScoreBar value={val} />
                </div>
              ))}
            </div>

            {/* Price adjustment */}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-caption text-muted-foreground">Price Adj.</span>
              <span className={`text-xs font-mono-data font-semibold ${comp?.priceAdjustment >= 0 ? "text-secondary" : "text-accent"}`}>
                {comp?.priceAdjustment >= 0 ? "+" : ""}
                {comp?.priceAdjustment?.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
              </span>
            </div>

            {/* Reasons */}
            <div className="flex flex-wrap gap-1 mt-2">
              {comp?.reasons?.map((r) => (
                <span key={r} className="text-xs font-caption bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{r}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-border">
        <Button variant="ghost" size="sm" iconName="ChevronRight" iconPosition="right" iconSize={13} onClick={onViewAll} fullWidth>
          View All Comparables
        </Button>
      </div>
    </div>
  );
}